import { ProtocolStepPayload } from '@yoshu-lib/core/api/protocol-sessions/protocol-sessions.model'

export interface StepAction {
  trigger: string
  label?: string
  icon?: string
  variant?: string
}

export interface StepOption {
  label: string
  value: unknown
}

export interface StepHistoryUpdateResult {
  history: { step_id: string; context_snapshot: Record<string, unknown> }[]
  lastStepId: string
  restoredContext?: Record<string, unknown>
}

export const buildActionButtons = (
  step: ProtocolStepPayload | null,
  availableTransitions: string[]
): StepAction[] => {
  if (!step) return []

  const metadataActions = step.actions ?? step.metadata?.['actions'] ?? []
  const metaByTrigger = new Map<string, StepAction>()
  if (Array.isArray(metadataActions)) {
    for (const action of metadataActions) {
      if (!action || typeof action !== 'object') continue
      const record = action as StepAction
      if (!record.trigger) continue
      metaByTrigger.set(record.trigger, record)
    }
  }

  const seen = new Set<string>()
  const uniqueTransitions = availableTransitions.filter((trigger) => {
    if (!trigger) return false
    if (seen.has(trigger)) return false
    seen.add(trigger)
    return true
  })

  return uniqueTransitions.map((trigger) => {
    const meta = metaByTrigger.get(trigger)
    if (meta) {
      return meta
    }

    return {
      trigger,
      label: formatActionLabel(trigger),
      variant: trigger === 'back' ? 'outline' : 'default'
    }
  })
}

export const updateStepHistory = (params: {
  currentStepId: string
  lastStepId: string | null
  history: { step_id: string; context_snapshot: Record<string, unknown> }[]
  localContext: Record<string, unknown>
  stepOrder?: string[]
}): StepHistoryUpdateResult => {
  const { currentStepId, lastStepId, history, localContext, stepOrder = [] } = params
  let nextHistory = [...history]

  if (lastStepId && lastStepId !== currentStepId) {
    nextHistory = upsertHistoryEntry(nextHistory, lastStepId, localContext)

    if (stepOrder.length > 0) {
      const lastIndex = stepOrder.indexOf(lastStepId)
      const currentIndex = stepOrder.indexOf(currentStepId)

      if (lastIndex >= 0 && currentIndex >= 0 && currentIndex > lastIndex + 1) {
        for (let index = lastIndex + 1; index < currentIndex; index += 1) {
          const skippedStepId = stepOrder[index]
          nextHistory = upsertHistoryEntry(nextHistory, skippedStepId, localContext)
        }
      }
    }
  }

  const existingIndex = nextHistory.findIndex((item) => item.step_id === currentStepId)
  if (existingIndex >= 0) {
    const entry = nextHistory[existingIndex]
    return {
      history: nextHistory.slice(0, existingIndex + 1),
      lastStepId: currentStepId,
      restoredContext: entry?.context_snapshot
    }
  }

  return {
    history: nextHistory,
    lastStepId: currentStepId
  }
}

export const updateContextForInput = (params: {
  step: ProtocolStepPayload | null
  value: unknown
  context: Record<string, unknown>
}): Record<string, unknown> => {
  const { step, value, context } = params
  if (!step || step.step_type !== 'input') return { ...context }

  const config =
    step.config && typeof step.config === 'object' ? (step.config as Record<string, unknown>) : {}
  const savesTo = typeof config['saves_to'] === 'string' ? config['saves_to'] : ''
  if (!savesTo) return { ...context }

  const path = savesTo.startsWith('context.') ? savesTo.slice('context.'.length) : savesTo
  if (!path) return { ...context }

  return setValueAtPath(context, path, value)
}

const upsertHistoryEntry = (
  history: { step_id: string; context_snapshot: Record<string, unknown> }[],
  stepId: string,
  context: Record<string, unknown>
) => {
  const entry = {
    step_id: stepId,
    context_snapshot: cloneContextSnapshot(context)
  }
  const index = history.findIndex((item) => item.step_id === stepId)
  if (index >= 0) {
    const next = [...history]
    next[index] = entry
    return next
  }
  return [...history, entry]
}

const setValueAtPath = (
  source: Record<string, unknown>,
  path: string,
  value: unknown
): Record<string, unknown> => {
  const keys = path.split('.').filter(Boolean)
  if (keys.length === 0) return { ...source }

  const result: Record<string, unknown> = { ...source }
  let cursor: Record<string, unknown> = result

  for (let index = 0; index < keys.length - 1; index += 1) {
    const key = keys[index]
    const existing = cursor[key]
    const next =
      existing && typeof existing === 'object' && !Array.isArray(existing)
        ? { ...(existing as Record<string, unknown>) }
        : {}
    cursor[key] = next
    cursor = next
  }

  cursor[keys[keys.length - 1]] = value
  return result
}

const cloneContextSnapshot = (source: Record<string, unknown>): Record<string, unknown> => {
  try {
    return JSON.parse(JSON.stringify(source)) as Record<string, unknown>
  } catch {
    return { ...source }
  }
}

const formatActionLabel = (trigger: string): string => {
  if (trigger === 'next') {
    return 'Next'
  }

  if (trigger === 'back') {
    return 'Back'
  }

  const raw = trigger
    .replace(/^action:/, '')
    .replace(/[_-]+/g, ' ')
    .trim()

  if (!raw) return 'Action'

  return raw.charAt(0).toUpperCase() + raw.slice(1)
}
